export interface User {
  id: number;
  email: string | null;
  name?: string | null;
  isAdmin?: boolean;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  company: string | null;
  emailVerified: boolean;
  createdAt: string;
  lastSignInAt: string | null;
  lastSignInCity: string | null;
  lastSignInCountryCode: string | null;
  accountPaused: boolean;
  plan: string;
  isTrial: boolean;
  trialExpired: boolean;
  trialEndsAt: string | null;
  trialMaxOrgs: number | null;
  managed: boolean;
  organisations: Array<{ connectionId: string; tenantName: string; connectedAt: string }>;
}

export interface XeroTenant {
  tenantId: string;
  tenantName: string;
  isActive: boolean;
  // True iff InvoicePolish has AU payroll scopes for this Xero connection.
  // When false, do not load payroll data; show the reconnect banner instead.
  hasPayrollAccess: boolean;
  // ISO timestamp of when this Xero connection was authorised in InvoicePolish.
  // Optional — older connections / sibling apps may not surface it.
  connectedAt?: string | null;
}

export interface XeroSuperMembership {
  SuperMembershipID: string;
  superMembershipID?: string;
  SuperFundID?: string;
  SuperFundName?: string;
  MemberNumber?: string;
  EmployerContributionRate?: number;
  Status?: string;
}

export interface XeroEmployee {
  EmployeeID: string;
  FirstName: string;
  LastName: string;
  Status: string;
  PayrollCalendarID?: string;
  PayrollCalendar?: {
    PayrollCalendarID?: string;
    Name?: string;
    CalendarType?: string;
  };
  payrollCalendarID?: string;
  payrollCalendar?: {
    payrollCalendarID?: string;
    name?: string;
    calendarType?: string;
  };
  SuperMemberships?: XeroSuperMembership[];
  superMemberships?: XeroSuperMembership[];
  PayTemplate?: {
    SuperLines?: Array<{
      SuperMembershipID?: string;
      superMembershipID?: string;
      ContributionType?: string;
      CalculationType?: string;
      ExpenseAccountCode?: string;
      LiabilityAccountCode?: string;
    }>;
  };
  payTemplate?: {
    superLines?: Array<{
      SuperMembershipID?: string;
      superMembershipID?: string;
      ContributionType?: string;
      CalculationType?: string;
      ExpenseAccountCode?: string;
      LiabilityAccountCode?: string;
    }>;
  };
  SuperDataSource?: 'summary' | 'full-detail' | 'cache';
  SuperDetailLoaded?: boolean;
  superDetailLoaded?: boolean;
}

export type SuperCheckStatus =
  | 'not_checked'
  | 'checking'
  | 'found'
  | 'missing_after_full_detail'
  | 'error';

export type EmployeeSuperValidationStatus =
  | 'UNKNOWN'
  | 'CHECKING'
  | 'VALID'
  | 'MISSING'
  | 'ERROR';

export interface EmployeeSuperValidation {
  employeeId: string;
  fullName?: string;
  superStatus: EmployeeSuperValidationStatus;
  checkedAt?: string;
  superMemberships?: Array<{
    superMembershipId: string;
    superFundId?: string;
    memberNumber?: string;
  }>;
  message?: string | null;
  retryAfterSeconds?: number;
  requestId?: string;
}

export interface EmployeeSuperStatusResult {
  status: SuperCheckStatus;
  hasPayTemplateSuperLines: boolean;
  payTemplateSuperLineCount: number;
  hasSuperMemberships: boolean;
  superMembershipCount: number;
  fullDetailLoaded: boolean;
}

function hasMembershipId(item: XeroSuperMembership | { SuperMembershipID?: string; superMembershipID?: string }): boolean {
  return !!(item.SuperMembershipID ?? item.superMembershipID);
}

export function evaluateEmployeeSuperStatus(employee?: XeroEmployee | null): EmployeeSuperStatusResult {
  const payTemplateSuperLines = employee?.PayTemplate?.SuperLines ?? employee?.payTemplate?.superLines ?? [];
  const superMemberships = employee?.SuperMemberships ?? employee?.superMemberships ?? [];

  const hasPayTemplateSuperLines =
    Array.isArray(payTemplateSuperLines) && payTemplateSuperLines.length > 0;
  const hasSuperMemberships =
    Array.isArray(superMemberships) && superMemberships.length > 0;
  const fullDetailLoaded = employee?.SuperDetailLoaded === true || employee?.superDetailLoaded === true;

  if (hasPayTemplateSuperLines || hasSuperMemberships) {
    return {
      status: 'found',
      hasPayTemplateSuperLines,
      payTemplateSuperLineCount: Array.isArray(payTemplateSuperLines) ? payTemplateSuperLines.length : 0,
      hasSuperMemberships,
      superMembershipCount: Array.isArray(superMemberships) ? superMemberships.length : 0,
      fullDetailLoaded,
    };
  }

  if (!fullDetailLoaded) {
    return {
      status: 'checking',
      hasPayTemplateSuperLines,
      payTemplateSuperLineCount: 0,
      hasSuperMemberships,
      superMembershipCount: 0,
      fullDetailLoaded,
    };
  }

  return {
    status: 'missing_after_full_detail',
    hasPayTemplateSuperLines,
    payTemplateSuperLineCount: 0,
    hasSuperMemberships,
    superMembershipCount: 0,
    fullDetailLoaded,
  };
}

// SuperMembershipID is all we need to post a super line to a payslip
export function employeeHasValidSuper(employee: XeroEmployee): boolean {
  return evaluateEmployeeSuperStatus(employee).status === 'found';
}

export function activeSuper(employee: XeroEmployee): XeroSuperMembership | undefined {
  return (employee.SuperMemberships ?? employee.superMemberships ?? []).find(hasMembershipId);
}

export function employeeSuperStatus(employee: XeroEmployee): SuperCheckStatus {
  return evaluateEmployeeSuperStatus(employee).status;
}

export function employeePayrollCalendarId(employee?: XeroEmployee | null): string | undefined {
  return (
    employee?.PayrollCalendarID ??
    employee?.PayrollCalendar?.PayrollCalendarID ??
    (employee as { PayrollCalendarId?: string } | null | undefined)?.PayrollCalendarId ??
    employee?.payrollCalendarID ??
    (employee as { payrollCalendarId?: string } | null | undefined)?.payrollCalendarId ??
    employee?.payrollCalendar?.payrollCalendarID
  );
}

export function employeePayrollCalendarName(employee?: XeroEmployee | null): string | undefined {
  return employee?.PayrollCalendar?.Name ?? employee?.payrollCalendar?.name;
}

export function employeePayrollCalendarType(employee?: XeroEmployee | null): string | undefined {
  return (
    employee?.PayrollCalendar?.CalendarType ??
    (employee?.PayrollCalendar as { CalendarType?: string; calendarType?: string } | undefined)?.calendarType ??
    employee?.payrollCalendar?.calendarType
  )?.toUpperCase();
}

export function calendarTypeLabel(type?: string | null): string {
  if (!type) return 'Unknown';
  const normalised = type.toUpperCase();
  if (normalised === 'MONTHLY') return 'Monthly';
  if (normalised === 'FORTNIGHTLY') return 'Fortnightly';
  if (normalised === 'WEEKLY') return 'Weekly';
  return normalised.charAt(0) + normalised.slice(1).toLowerCase();
}

export interface ImportLine {
  id: number;
  importId: number;
  contractorName: string;
  debitTotal: string;
  superRate: string;
  superAmount: string;
  xeroEmployeeId: string | null;
  xeroEmployeeName: string | null;
  status: string;
}

export interface ImportRecord {
  id: number;
  userId: number;
  tenantId: string;
  filename: string;
  reportMonth: string | null;
  reportYear: string | null;
  status: string;
  createdAt: string;
}

export interface UploadResult {
  import: ImportRecord;
  lines: ImportLine[];
  reportMonth: string | null;
  reportYear: string | null;
  reportStartDate: string | null;
  reportEndDate: string | null;
  inferredCalendarType: 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY' | null;
  warnings: string[];
}

export interface PayRunResult {
  payrun: {
    id: number;
    xeroPayRunId: string;
    payPeriodStartDate: string;
    payPeriodEndDate: string;
    paymentDate: string;
    totalSuper: string;
  };
  xeroPayRunId: string;
  xeroUrl: string;
  results: Array<{
    contractorName: string;
    employeeId: string;
    superAmount: number;
    status: string;
    error?: string;
  }>;
  totalSuper: number;
  payRunReused?: boolean;
  payRunStatus?: string;
  expectedSuperTotal?: number;
  payPeriodStartDate: string;
  payPeriodEndDate: string;
  success: boolean;
}

export interface QuickSuperLine {
  id?: number;
  contractorName: string;
  debitTotal: string;
  superRate: string;
  superAmount: string;
}

export interface QuickSuperMember {
  id: number;
  name: string;
  fundName: string | null;
  memberId: string | null;
}

export interface QuickSuperMatch {
  memberId: number | null;
  source: 'saved' | 'auto' | null;
}

export interface QuickSuperMembersImportResult {
  kind: 'quicksuper_members';
  total: number;
  created: number;
  updated: number;
}

export interface QuickSuperSummary {
  lines: QuickSuperLine[];
  total: number;
  finalised: boolean;
}

export interface XeroPayrollCalendar {
  PayrollCalendarID: string;
  Name: string;
  CalendarType: string;
  StartDate: string;
  PaymentDate: string;
}

export interface XeroCalendarNextPeriod {
  payPeriodStartDate: string;
  payPeriodEndDate: string;
  source: 'blocking-draft' | 'computed' | 'calendar-anchor';
  blockingPayRunId?: string;
  blockingStatus?: string;
}

export interface TenantExpectedPeriod extends Partial<XeroCalendarNextPeriod> {
  payrollCalendarId: string | null;
  payrollCalendarName?: string | null;
}

export interface PastRunLineResult {
  contractorName: string;
  debitTotal: string;
  superRate: string;
  superAmount: string;
  xeroEmployeeId: string | null;
  xeroEmployeeName: string | null;
  quickSuperMemberName?: string | null;
  quickSuperFundName?: string | null;
  status: string;
  error: string | null;
}

export interface PastRun {
  id: number;
  importId: number;
  userId: string;
  tenantId: string;
  tenantName: string | null;
  xeroPayRunId: string;
  xeroPayRunUrl: string | null;
  payPeriodStartDate: string;
  payPeriodEndDate: string;
  paymentDate: string;
  payrollCalendarId: string | null;
  payrollCalendarName: string | null;
  payrollCalendarType: string | null;
  importFilename: string | null;
  reportMonth: string | null;
  reportYear: string | null;
  status: string;
  success: string | null;
  totalSuper: string;
  lineResults: PastRunLineResult[] | null;
  quickSuperLines: PastRunLineResult[] | null;
  finalisedAt: string | null;
  createdAt: string;
  // Original uploaded report metadata (Excel/CSV/PDF). hasSourceFile tells the
  // UI whether to render the "Download source" button.
  sourceFilename: string | null;
  sourceMimetype: string | null;
  hasSourceFile: boolean;
}

// Workflow states for the main page
export type WorkflowStep =
  | 'upload'
  | 'review'
  | 'confirm'
  | 'processing'
  | 'success'
  | 'quicksuper'
  | 'quicksuper_preview';

// Automated Xero Report workflow types

export interface XeroAccountCode {
  Code: string;
  Name: string;
  Type: string;
  Status: string;
}

export interface SavedAccountCode {
  id: number;
  userId: string;
  tenantId: string;
  accountCode: string;
  accountName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionRow {
  accountCode: string;
  accountName: string | null;
  contactName: string;
  date: string;
  source: string;
  description: string;
  reference: string;
  debit: number;
  credit: number;
  gross: number;
  gst: number;
}

export interface ReportSection {
  accountCode: string;
  accountName: string | null;
  transactions: TransactionRow[];
  subtotalDebit: number;
  subtotalGross: number;
  subtotalGst: number;
  superRate: number;
  superAmount: number;
}

export interface TransactionReport {
  organisationName: string | null;
  startDate: string;
  endDate: string;
  sections: ReportSection[];
  grandTotalDebit: number;
  grandTotalGross: number;
  grandTotalGst: number;
  grandSuperAmount: number;
}
